/**
 * SEO helpers for RPA use-case pages under /rpa-use-cases/{domain-slug}.
 */
import {
  SITE_BASE_URL,
  ORG,
  buildDomainFaqs,
  buildItemListJsonLd,
  buildCombinedFaqJsonLd,
  enrichUseCaseForSeo,
} from "./agenticSeoUtils";

export const RPA_BASE_PATH = "/rpa-use-cases";

export function enrichRpaUseCaseForSeo(uc, { domainSlug, headline }) {
  return enrichUseCaseForSeo(uc, {
    domainSlug,
    headline,
    mode: "rpa",
    basePath: RPA_BASE_PATH,
  });
}

export function buildRpaBreadcrumbJsonLd(domainIntro, domainSlug) {
  return {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: [
      { "@type": "ListItem", position: 1, name: "Home", item: SITE_BASE_URL },
      {
        "@type": "ListItem",
        position: 2,
        name: "RPA Use Cases",
        item: `${SITE_BASE_URL}${RPA_BASE_PATH}`,
      },
      {
        "@type": "ListItem",
        position: 3,
        name: domainIntro.title,
        item: `${SITE_BASE_URL}${RPA_BASE_PATH}/${domainSlug}`,
      },
    ],
  };
}

export function buildRpaWebPageJsonLd(domainIntro, useCases, pageName, domainSlug) {
  return {
    "@context": "https://schema.org",
    "@type": "WebPage",
    name: pageName || `${domainIntro.title} Use Cases`,
    description: domainIntro.seoDescription,
    url: `${SITE_BASE_URL}${RPA_BASE_PATH}/${domainSlug}`,
    inLanguage: "en",
    isPartOf: {
      "@type": "WebSite",
      name: "Surens Inffotek",
      url: SITE_BASE_URL,
    },
    publisher: ORG,
    about: {
      "@type": "Thing",
      name: `${domainIntro.title} Automation`,
      description: domainIntro.content,
    },
    mainEntity: buildItemListJsonLd(useCases, `${domainIntro.title} Use Cases`),
  };
}

export function buildRpaDomainSeoBundle({
  domainSlug,
  domainIntro,
  useCases,
  getHeadline = (uc) => uc.seoTitle || uc.title,
}) {
  const useCasesSeo = useCases.map((uc) =>
    enrichRpaUseCaseForSeo(uc, { domainSlug, headline: getHeadline(uc) })
  );
  const listName = `${domainIntro.title} Use Cases`;
  const domainFaqs = buildDomainFaqs(domainIntro, useCasesSeo, "rpa");

  return {
    useCasesSeo,
    domainFaqs,
    itemListJsonLd: buildItemListJsonLd(useCasesSeo, listName),
    faqJsonLd: buildCombinedFaqJsonLd(domainFaqs, useCasesSeo),
    breadcrumbJsonLd: buildRpaBreadcrumbJsonLd(domainIntro, domainSlug),
    webPageJsonLd: buildRpaWebPageJsonLd(domainIntro, useCasesSeo, listName, domainSlug),
  };
}

export const RPA_HUB_META = {
  title: "Robotic Process Automation (RPA) Services & Use Cases | Surens Inffotek",
  description:
    "Robotic Process Automation (RPA) and Document AI use cases across finance, manufacturing, HR, healthcare, logistics, retail, insurance, telecom, public sector, real estate, hospitality, agriculture, IT, aviation, media, energy, and education. 149 structured challenge/solution entries for enterprises in India, UAE, KSA, UK, and USA.",
  canonicalPath: "/robotic-process-automation",
};

export function buildRpaHubWebPageJsonLd() {
  return {
    "@context": "https://schema.org",
    "@type": "WebPage",
    name: "Robotic Process Automation (RPA) Use Cases",
    description: RPA_HUB_META.description,
    url: `${SITE_BASE_URL}${RPA_HUB_META.canonicalPath}`,
    inLanguage: "en",
    isPartOf: {
      "@type": "WebSite",
      name: "Surens Inffotek",
      url: SITE_BASE_URL,
    },
    publisher: ORG,
    about: {
      "@type": "Thing",
      name: "Robotic Process Automation",
      description:
        "Enterprise RPA services with Document AI, workflow automation, UiPath, Power Automate, and SAP integration.",
    },
    mainEntity: {
      "@type": "ItemList",
      name: "RPA Use Case Domains",
      numberOfItems: 17,
      itemListElement: [
        {
          "@type": "ListItem",
          position: 1,
          name: "RPA Use Cases Catalog",
          item: `${SITE_BASE_URL}${RPA_BASE_PATH}`,
        },
        {
          "@type": "ListItem",
          position: 2,
          name: "LLM RPA Catalog",
          item: `${SITE_BASE_URL}/llms-rpa.txt`,
        },
      ],
    },
  };
}

